import { useState, type FormEvent, type ReactNode } from 'react'
import {
  currencySymbol,
  formatMoney,
  inBasket,
  moveItemPatch,
  type Store,
  type TripItem,
  type TripItemPatch,
} from '@sp/shared'
import { formatQty, intoStore, parsePriceInput, priceInputValue, storeSub, unitPrice } from '../lib/format.ts'
import { errorMessage } from '../lib/useResource.ts'
import { Sheet } from './Sheet.tsx'
import { Icon } from './Icon.tsx'
import { Button, Divider, MenuItem, Paragraph, Stepper, TextCol, TextField } from './ui.tsx'

export interface ItemSheetProps {
  item: TripItem | null
  /** plan = ocekavana cena; shop = stavy a skutecna cena. */
  mode: 'plan' | 'shop'
  store: Store | null
  /** Obchody, do kterych jde polozku presunout (ostatni na trase). */
  moveTargets: readonly Store[]
  onClose(): void
  onChange(itemId: string, patch: TripItemPatch): void
  /** Odebrani s „Vrátit" resi volajici. */
  onRemove(item: TripItem): void
  onSaveToTemplate?: ((item: TripItem) => Promise<void>) | undefined
  /** Sekce Slevy (useTripDeals.itemDeals). */
  deals?: ReactNode
  /** Po naskenovani kodu se panel otevre rovnou s cenou. */
  startWithPrice?: boolean | undefined
}

/**
 * Detail polozky nakupu (navrh v3). V obchode jsou nahore dve velka rozhodnuti
 * („Do košíku" / „Nemají to") a cena, pri planovani mnozstvi a cena; Slevy
 * a presun jsou v obou rezimech stejne.
 */
export function ItemSheet(props: ItemSheetProps) {
  const { item, store, onClose } = props
  const currency = item ? (item.expectedCurrency ?? store?.currency ?? null) : null
  const price = item ? unitPrice(item.expectedPriceMinor, currency, item.unit) : null
  const subtitle = item
    ? [store?.name ?? 'Kdekoli', formatQty(item.qty, item.unit), price].filter(Boolean).join(' · ')
    : undefined

  return (
    <Sheet open={item !== null} onClose={onClose} title={item?.name ?? ''} subtitle={subtitle}>
      {item && <ItemActions key={item.id} {...props} item={item} />}
    </Sheet>
  )
}

function ItemActions({
  item,
  mode,
  store,
  moveTargets,
  onClose,
  onChange,
  onRemove,
  onSaveToTemplate,
  deals,
  startWithPrice = false,
}: ItemSheetProps & { item: TripItem }) {
  const shownPrice = mode === 'shop' ? (item.actualPriceMinor ?? item.expectedPriceMinor) : item.expectedPriceMinor
  const [editor, setEditor] = useState<'price' | 'name' | 'move' | null>(null)
  const [priceDraft, setPriceDraft] = useState(() =>
    mode === 'plan' || startWithPrice ? priceInputValue(shownPrice) : '',
  )
  const [nameDraft, setNameDraft] = useState(item.name)
  const [noteDraft, setNoteDraft] = useState(item.note ?? '')
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  const currency = item.expectedCurrency ?? store?.currency ?? null
  const symbol = currency ? ` (${currencySymbol(currency)})` : ''
  const bought = inBasket(item)
  const apply = (patch: TripItemPatch, close = true) => {
    onChange(item.id, patch)
    if (close) onClose()
  }

  const moveTo = (target: Store) =>
    apply({ ...moveItemPatch(item, target), status: 'pending', actualPriceMinor: null })

  function savePrice(e: FormEvent) {
    e.preventDefault()
    const minor = parsePriceInput(priceDraft)
    if (minor === 'invalid') return setError('Cena musí mít tvar 4,99')
    if (mode === 'shop') {
      if (minor === null) return setError('Napiš cenu, za kterou to v obchodě je')
      // Stejna cena, jakou cekala (typicky po naskenovani), neni „jina cena" — jen v kosiku.
      apply(
        minor === item.expectedPriceMinor
          ? { status: 'found', actualPriceMinor: null }
          : { status: 'found_other_price', actualPriceMinor: minor, expectedCurrency: currency },
      )
      return
    }
    // Cena zadana rukou uz neni z akce.
    apply({ expectedPriceMinor: minor, expectedCurrency: currency, ...(item.deal ? { deal: null } : {}) }, false)
    setEditor(null)
    setError(null)
  }

  function saveName(e: FormEvent) {
    e.preventDefault()
    const name = nameDraft.trim()
    if (!name) return setError('Název nesmí být prázdný')
    apply({ name, note: noteDraft.trim() || null }, false)
    setEditor(null)
    setError(null)
  }

  async function saveToTemplate() {
    if (!onSaveToTemplate) return
    setSaving(true)
    setError(null)
    try {
      await onSaveToTemplate(item)
      onClose()
    } catch (err) {
      setError(errorMessage(err))
    } finally {
      setSaving(false)
    }
  }

  const toggle = (name: typeof editor) => {
    setError(null)
    setEditor(editor === name ? null : name)
  }

  return (
    <div className="stack">
      {mode === 'shop' && (
        <>
          {bought ? (
            <Button full icon="refresh" onClick={() => apply({ status: 'pending', actualPriceMinor: null })}>
              Vrátit mezi nekoupené
            </Button>
          ) : (
            <div className="grid-2">
              <Button kind="primary" icon="check" onClick={() => apply({ status: 'found', actualPriceMinor: null })}>
                Do košíku
              </Button>
              {item.status === 'not_found' ? (
                <Button icon="refresh" onClick={() => apply({ status: 'pending' })}>
                  Hledat dál
                </Button>
              ) : (
                <Button icon="x_circle" className="btn-notfound" onClick={() => apply({ status: 'not_found', actualPriceMinor: null })}>
                  Nemají to
                </Button>
              )}
            </div>
          )}
          <form className="add-row align-end" onSubmit={savePrice}>
            <TextField
              className="grow"
              label={`Cena tady${symbol}`}
              value={priceDraft}
              onChange={(e) => setPriceDraft(e.target.value)}
              inputMode="decimal"
              placeholder={shownPrice !== null ? priceInputValue(shownPrice) : '4,99'}
              autoFocus={startWithPrice}
              // Pole se otevira predvyplnene; sestra cenu chce rovnou prepsat, ne mazat.
              onFocus={(e) => e.currentTarget.select()}
            />
            <Button type="submit">{bought ? 'Uložit cenu' : 'Za tuhle cenu'}</Button>
          </form>
        </>
      )}
      {item.note && <Paragraph tone="fg">{item.note}</Paragraph>}
      {deals && (
        <>
          <Divider />
          {deals}
        </>
      )}
      <Divider />
      <div className="qty-row">
        <span>Množství</span>
        <Stepper
          value={formatQty(item.qty, item.unit)}
          onDecrement={() => apply({ qty: item.qty > 1 ? item.qty - 1 : item.qty }, false)}
          onIncrement={() => apply({ qty: item.qty + 1 }, false)}
          decrementLabel={`Méně: ${item.name}`}
          incrementLabel={`Více: ${item.name}`}
        />
      </div>
      <div className="menu">
        {mode === 'plan' && (
          <>
            <MenuItem
              icon="tag"
              iconTone="muted"
              label="Cena"
              aria-expanded={editor === 'price'}
              onClick={() => {
                setPriceDraft(priceInputValue(shownPrice))
                toggle('price')
              }}
              right={
                <span className="menu-right">
                  {shownPrice !== null && currency ? formatMoney(shownPrice, currency) : 'bez ceny'}
                  <Icon name={editor === 'price' ? 'chevron_up' : 'chevron_down'} size={18} />
                </span>
              }
            />
            {editor === 'price' && (
              <form className="inline-editor" onSubmit={savePrice}>
                <TextField
                  label={`Očekávaná cena${symbol}`}
                  value={priceDraft}
                  onChange={(e) => setPriceDraft(e.target.value)}
                  inputMode="decimal"
                  placeholder="4,99"
                  autoFocus
                  onFocus={(e) => e.currentTarget.select()}
                />
                <Button kind="primary" type="submit">
                  Uložit
                </Button>
              </form>
            )}
          </>
        )}
        {/* Jeden dalsi obchod = primy presun. Vic obchodu = jedna polozka, ktera
            rozbali vyber — jinak by panel s kazdym obchodem skupiny rostl. */}
        {moveTargets.length === 1 && moveTargets[0] && (
          <MenuItem
            icon="arrow_right"
            iconTone="muted"
            label={`Přesunout ${intoStore(moveTargets[0].name)}`}
            onClick={() => moveTo(moveTargets[0]!)}
          />
        )}
        {moveTargets.length > 1 && (
          <>
            <MenuItem
              icon="arrow_right"
              iconTone="muted"
              label="Přesunout do jiného obchodu"
              aria-expanded={editor === 'move'}
              onClick={() => toggle('move')}
              right={
                <span className="menu-right">
                  <Icon name={editor === 'move' ? 'chevron_up' : 'chevron_down'} size={18} />
                </span>
              }
            />
            {editor === 'move' && (
              <div className="store-picks inline-editor-block">
                {moveTargets.map((target) => (
                  <button key={target.id} type="button" className="store-pick" onClick={() => moveTo(target)}>
                    <TextCol title={target.name} sub={storeSub(target)} />
                    <Icon name="chevron_right" size={18} className="tone-muted" />
                  </button>
                ))}
              </div>
            )}
          </>
        )}
        <MenuItem
          icon="pencil"
          iconTone="muted"
          label="Název a poznámka"
          aria-expanded={editor === 'name'}
          onClick={() => toggle('name')}
        />
        {editor === 'name' && (
          <form className="stack-8 inline-editor-block" onSubmit={saveName}>
            <TextField label="Název" value={nameDraft} onChange={(e) => setNameDraft(e.target.value)} maxLength={200} autoFocus />
            <TextField
              label="Poznámka"
              value={noteDraft}
              onChange={(e) => setNoteDraft(e.target.value)}
              maxLength={500}
              placeholder="Třeba značka nebo velikost balení"
            />
            <Button kind="primary" type="submit" className="self-start">
              Uložit
            </Button>
          </form>
        )}
        {!item.catalogItemId && onSaveToTemplate && (
          <MenuItem
            icon="list_check"
            iconTone="muted"
            label="Uložit do Pravidelného nákupu"
            disabled={saving}
            onClick={() => void saveToTemplate()}
          />
        )}
      </div>
      {error && <Paragraph tone="error">{error}</Paragraph>}
      <Button
        kind="danger"
        icon="trash"
        className="self-start"
        onClick={() => {
          onRemove(item)
          onClose()
        }}
      >
        Odebrat z nákupu
      </Button>
    </div>
  )
}
